import React from "react";
import styles from "./styles.css";
const DRAG_PIXEL_THRESHOLD = 10;
const useDraggable = _ref => {
  let {
    onDrag,
    onDragEnd
  } = _ref;
  const slideElementRef = React.useRef(null);
  const originX = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const doDrag = React.useCallback(distance => {
    onDrag({
      distance,
      target: slideElementRef.current
    });
    setIsDragging(true);
  }, [onDrag]);
  const endDrag = React.useCallback(() => {
    if (isDragging) {
      onDragEnd();
    }
    setIsDragging(false);
    originX.current = null;
    slideElementRef.current = null;
  }, [onDragEnd, isDragging]);

  /**
   * Global handlers
   */
  React.useEffect(() => {
    const onMouseOut = evt => {
      if (originX.current !== null && (!evt.relatedTarget || evt.relatedTarget.nodeName === 'HTML')) {
        endDrag();
      }
    };
    const onMouseUp = evt => {
      endDrag();
      evt.preventDefault();
    };
    const onMouseMove = evt => {
      if (originX.current === null) {
        return;
      }
      const difference = originX.current - evt.clientX;
      if (Math.abs(difference) >= 15) {
        doDrag(difference);
        evt.preventDefault();
      }
    };
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseout', onMouseOut);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [isDragging, originX, onDrag, onDragEnd, doDrag, endDrag]);

  /**
   * Draggable container handlers
   */
  return React.useMemo(() => {
    const onMouseDown = evt => {
      originX.current = evt.clientX;
      slideElementRef.current = evt.target;
      evt.preventDefault();
    };
    const onDragStart = evt => {
      evt.preventDefault();
    };
    const onTouchStart = evt => {
      originX.current = evt.touches[0].clientX;
      slideElementRef.current = evt.target;
      evt.preventDefault();
    };
    const onTouchMove = evt => {
      if (originX.current === null) {
        return;
      }
      const difference = originX.current - evt.touches[0].clientX;
      if (Math.abs(difference) < DRAG_PIXEL_THRESHOLD) {
        return;
      }
      doDrag(difference);
      evt.preventDefault();
    };
    return {
      onDragStart,
      onTouchStart,
      onTouchMove,
      onTouchEnd: endDrag,
      onMouseDown,
      // Disable pointer events to avoid accidentally clicking on links in carousel
      // While intention is to scroll
      className: isDragging ? styles.disablePointerEvents : ''
    };
  }, [isDragging, originX, doDrag, endDrag]);
};
export default useDraggable;